import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Printer, Download, Search } from 'lucide-react';
import { PAGE_VISIBILITY_ITEMS } from '../lib/pageVisibility';

// All known permission keys with friendly labels (kept in sync with Team.js / Permissions.js).
const PERMISSION_LABELS = {
  // Jobs
  create_jobs: 'Create Jobs',
  edit_jobs: 'Edit Jobs',
  delete_jobs: 'Delete Jobs',
  assign_jobs: 'Assign Jobs',
  // Proposals
  view_proposals: 'View Proposals',
  create_proposals: 'Create Proposals',
  // Visibility
  view_all_jobs: 'View All Jobs (Dashboard)',
  view_reports: 'View Reports',
  view_financials: 'View Financials',
  office_access: 'Office Access',
  // Management
  manage_team: 'Manage Team',
  delete_team_members: 'Delete Team Members',
  manage_services: 'Manage Services',
  manage_contacts: 'Manage Contacts',
  manage_equipment: 'Manage Equipment',
  manage_calendar_events: 'Manage Calendar Events',
  // Expenses
  expense_any_job: 'Expense to Any Job',
  // Approvals
  approve_field_reports: 'Approve Field Reports',
  approve_timesheets: 'Approve Timesheets',
  approve_expenses: 'Approve Expenses',
  // Backend perm keys (full snake_case as stored in the DB) — covers the
  // maintenance / equipment / notification flags that don't follow the
  // shorter "approve_*" naming above.
  can_approve_field_reports: 'Approve Field Reports',
  can_approve_maintenance_forms: 'Approve Maintenance Forms',
  can_approve_equipment_forms: 'Approve Equipment Forms',
  can_receive_approval_notifications: 'Receive Approval Notifications',
  can_receive_maintenance_notifications: 'Receive Maintenance Notifications',
};

const ROLE_BADGE = {
  admin: 'bg-red-100 text-red-700 border border-red-200',
  super_user: 'bg-purple-100 text-purple-700 border border-purple-200',
  manager: 'bg-blue-100 text-blue-700 border border-blue-200',
  technician: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
};

export default function PermissionsSnapshotDialog({ open, onOpenChange, members }) {
  const [search, setSearch] = useState('');

  // Sort: admins first, then managers, then technicians, alphabetical within each.
  const rows = useMemo(() => {
    const order = { super_user: 0, admin: 1, manager: 2, technician: 3 };
    const filtered = (members || []).filter(m => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (m.full_name || '').toLowerCase().includes(q)
          || (m.email || '').toLowerCase().includes(q)
          || (m.role || '').toLowerCase().includes(q);
    });
    return [...filtered].sort((a, b) => {
      const ra = order[a.role] ?? 99;
      const rb = order[b.role] ?? 99;
      if (ra !== rb) return ra - rb;
      return (a.full_name || '').localeCompare(b.full_name || '');
    });
  }, [members, search]);

  const handlePrint = () => window.print();

  const handleExport = () => {
    // Tab-separated for easy paste into Sheets / Excel
    const headers = ['Name', 'Email', 'Role', 'Active', 'Permissions (granted)', 'Hidden Pages'];
    const lines = [headers.join('\t')];
    for (const m of rows) {
      const perms = Object.entries(m.permissions || {})
        .filter(([_, v]) => v === true)
        .map(([k]) => PERMISSION_LABELS[k] || k)
        .join('; ');
      const hidden = Object.entries(m.page_visibility || {})
        .filter(([_, v]) => v === false)
        .map(([k]) => PAGE_VISIBILITY_ITEMS.find(p => p.key === k)?.label || k)
        .join('; ');
      lines.push([
        m.full_name || '',
        m.email || '',
        m.role || '',
        m.active === false ? 'No' : 'Yes',
        perms || '(none — admin auto-grants all)' ,
        hidden || '(all visible)',
      ].join('\t'));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `permissions-snapshot-${new Date().toISOString().slice(0, 10)}.tsv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col" data-testid="permissions-snapshot-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl">Permissions Snapshot</DialogTitle>
          <p className="text-sm text-slate-500">
            Side-by-side audit of every team member's role, granted permissions, and hidden pages.
          </p>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap pb-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, email, or role"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="snapshot-search"
            />
          </div>
          <Button onClick={handlePrint} variant="outline" size="sm" data-testid="snapshot-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button onClick={handleExport} variant="outline" size="sm" data-testid="snapshot-export-btn">
            <Download className="w-4 h-4 mr-2" /> Export TSV
          </Button>
        </div>

        <div className="overflow-auto flex-1 -mx-6 px-6">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 sticky top-0 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Name</th>
                <th className="text-left px-3 py-2 font-semibold">Role</th>
                <th className="text-center px-3 py-2 font-semibold">Active</th>
                <th className="text-left px-3 py-2 font-semibold">Permissions Granted</th>
                <th className="text-left px-3 py-2 font-semibold">Hidden Pages</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(m => {
                const isAdminish = m.role === 'admin' || m.role === 'super_user';
                const grantedPerms = Object.entries(m.permissions || {})
                  .filter(([_, v]) => v === true)
                  .map(([k]) => PERMISSION_LABELS[k] || k);
                const hiddenPages = Object.entries(m.page_visibility || {})
                  .filter(([_, v]) => v === false)
                  .map(([k]) => PAGE_VISIBILITY_ITEMS.find(p => p.key === k)?.label || k);

                return (
                  <tr
                    key={m.id}
                    className={`border-b border-slate-100 ${m.active === false ? 'opacity-50' : ''}`}
                    data-testid={`snapshot-row-${m.id}`}
                  >
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium">{m.full_name}</div>
                      <div className="text-xs text-slate-500">{m.email}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${ROLE_BADGE[m.role] || 'bg-slate-100 text-slate-700 border border-slate-200'}`}>
                        {m.role || 'unknown'}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top text-center">
                      {m.active === false
                        ? <span className="text-red-600 font-bold">NO</span>
                        : <span className="text-green-600">Yes</span>}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {isAdminish ? (
                        <span className="text-slate-500 italic text-xs">All permissions (auto-granted by role)</span>
                      ) : grantedPerms.length === 0 ? (
                        <span className="text-slate-400 italic text-xs">— no specific permissions —</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {grantedPerms.map(p => (
                            <span key={p} className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">
                              {p}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {isAdminish ? (
                        <span className="text-slate-500 italic text-xs">All pages visible</span>
                      ) : hiddenPages.length === 0 ? (
                        <span className="text-slate-400 italic text-xs">— all pages visible —</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {hiddenPages.map(p => (
                            <span key={p} className="text-[10px] bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded">
                              {p}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-slate-400">No matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="text-xs text-slate-400 pt-2 border-t border-slate-100 flex items-center justify-between">
          <span>{rows.length} {rows.length === 1 ? 'member' : 'members'} shown</span>
          <span>Snapshot taken {new Date().toLocaleString()}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
